import React, { useEffect, useState } from 'react';

import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { UsersAPI } from '../../lib/api';
import { isExternalAssignee } from '../../lib/userSearch.js';
import { assigneesOrSelf, formatAssigneeLabel } from '../../lib/assignees';
import { useAuth } from '../../lib/auth.jsx';
import {
  ROUTINE_FREQUENCIES,
  ROUTINE_WEEKDAYS,
  ROUTINE_MONTHS,
  ROUTINE_STATUS,
  TASK_TEAM,
  isIndividualUser,
} from '../../config';


/**
 * Shared "create / edit" form for a RoutineTask.
 *
 * Backed by ``RoutineTaskSerializer`` on the server — see
 * ``task_management/api_serializers.py``.  The user picks a
 * ``task_frequency`` (daily / weekly / monthly / annually) and the
 * form reveals ONLY the structured field(s) that frequency needs:
 *
 *   * weekly   -> day-of-week select
 *   * monthly  -> day-of-month (1-31) input
 *   * annually -> day + month pair
 *
 * The legacy ``cron_repeat_time`` field is read-only on the backend
 * (derived from the structured fields on save) so we never send it
 * from the client.  This is the one place the frequency-specific
 * inputs live; the list / edit pages just delegate here.
 *
 * Props:
 *   * ``initial``   — the row being edited (or ``null`` for create).
 *   * ``submitting``/``error`` — controlled by the parent so the
 *     dedicated edit/create pages can handle their own redirect on
 *     success.
 *   * ``onSubmit(values)`` — called with the cleaned payload ready
 *     for the API.  Returning a Promise lets the parent show a spinner.
 */
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function buildInitialValues(initial, selfId) {
  // ``end_date === null`` on an existing row means the routine has
  // been configured to "remain active always" — surface that in the
  // form by ticking the ``end_date_infinite`` checkbox and clearing
  // the date input.  Brand-new rows default to remain-active-always:
  // a daily routine whose "Active until" is today expires at the
  // next midnight cron tick and never shows up in the task list.
  const isExistingInfinite = initial && initial.end_date == null;
  const isCreate = !initial;
  return {
    title:               initial?.title ?? '',
    description:         initial?.description ?? '',
    start_date:          initial?.start_date ?? todayIso(),
    end_date:            initial?.end_date   ?? todayIso(),
    end_date_infinite:   isCreate || isExistingInfinite,
    estimated_hours:     initial?.estimated_hours ?? '',
    task_frequency:      initial?.task_frequency ?? 'daily',
    team:                initial?.team ?? 'individual',
    status:              initial?.status ?? 'not_started',
    cron_weekly_day:     initial?.cron_weekly_day ?? '',
    cron_monthly_date:   initial?.cron_monthly_date ?? '',
    cron_annually_date:  initial?.cron_annually_date ?? '',
    cron_annually_month: initial?.cron_annually_month ?? '',
    users:               assigneesOrSelf(initial?.users, selfId),
    approval_required:   !!initial?.approval_required,
  };
}

export default function RoutineTaskForm({ initial, onSubmit, submitting, error }) {
  const { user: currentUser } = useAuth();
  const selfId = currentUser?.id ?? null;
  const solo = isIndividualUser(currentUser);
  const [values, setValues] = useState(() => buildInitialValues(initial, selfId));
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  // Re-seed the form when ``initial`` changes — happens after the edit
  // page finishes loading the row (it's null on first render).
  useEffect(() => { setValues(buildInitialValues(initial, selfId)); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selfId == null) return;
    setValues((prev) => (
      prev.users && prev.users.length > 0 ? prev : { ...prev, users: [selfId] }
    ));
  }, [selfId]);

  useEffect(() => {
    if (solo) {
      setUsersLoading(false);
      return undefined;
    }
    let cancelled = false;
    setUsersLoading(true);
    // ``external: false`` drops auretics-users (portal / public-API
    // complaint accounts) server-side so they never appear as assignable
    // staff.  See TaskForm.jsx for why this cannot be a client filter.
    UsersAPI.list({ ordering: 'first_name', page_size: 200, external: 'false' })
      .then((data) => {
        if (cancelled) return;
        const list = data.results || data || [];
        setUsers(list.filter((u) => !isExternalAssignee(u)));
      })
      .catch((e) => !cancelled && setUsersError(e))
      .finally(() => !cancelled && setUsersLoading(false));
    return () => { cancelled = true; };
  }, [solo]);

  const set = (key) => (e) => {
    const target = e.target;
    let next;
    if (target.type === 'checkbox') {
      next = target.checked;
    } else if (target.multiple) {
      next = Array.from(target.selectedOptions).map((o) => Number(o.value));
    } else {
      next = target.value;
    }
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const submit = (e) => {
    e.preventDefault();

    // Build the payload.  Only send the structured field that matches
    // the chosen frequency — for the others we explicitly send
    // ``null`` so the backend wipes any stale value left over from a
    // previous frequency.  PATCH would otherwise leave them in place.
    const payload = {
      title:             values.title.trim(),
      description:       values.description?.trim() || '',
      start_date:        values.start_date,
      // ``end_date_infinite`` means the routine should keep firing
      // forever — represent that as a NULL on the wire so the
      // backend (RoutineTask model + cron worker) treats it as
      // "remain active always".
      end_date:          values.end_date_infinite ? null : values.end_date,
      estimated_hours:   values.estimated_hours === '' ? null : Number(values.estimated_hours),
      task_frequency:    values.task_frequency,
      team:              solo ? 'individual' : values.team,
      status:            values.status,
      approval_required: solo ? false : !!values.approval_required,
      users:             solo && selfId != null ? [selfId] : assigneesOrSelf(values.users, selfId),

      cron_weekly_day:     null,
      cron_monthly_date:   null,
      cron_annually_date:  null,
      cron_annually_month: null,
    };

    if (values.task_frequency === 'weekly') {
      payload.cron_weekly_day = values.cron_weekly_day || null;
    } else if (values.task_frequency === 'monthly') {
      payload.cron_monthly_date = values.cron_monthly_date
        ? Number(values.cron_monthly_date)
        : null;
    } else if (values.task_frequency === 'annually') {
      payload.cron_annually_date = values.cron_annually_date
        ? Number(values.cron_annually_date)
        : null;
      payload.cron_annually_month = values.cron_annually_month || null;
    }

    onSubmit(payload);
  };

  const freq = values.task_frequency;

  return (
    <form className="ty-form" onSubmit={submit}>
      <ErrorBox error={error} />
      <ErrorBox error={usersError} />

      <div className="ty-form__row">
        <label className="ty-field">
          <span>Title</span>
          <input
            className="ty-input"
            type="text"
            required
            value={values.title}
            onChange={set('title')}
            placeholder="e.g. Daily Standup"
          />
        </label>
      </div>

      <div className="ty-form__row">
        <label className="ty-field">
          <span>Description</span>
          <textarea
            className="ty-input"
            rows={3}
            value={values.description}
            onChange={set('description')}
            placeholder="What should be done each time this routine fires?"
          />
        </label>
      </div>

      <div className="ty-form__row">
        <label className="ty-field">
          <span>Repeats</span>
          <select
            className="ty-select"
            value={values.task_frequency}
            onChange={set('task_frequency')}
          >
            {ROUTINE_FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </label>

        {/* Frequency-specific inputs.  Only the ones that match the
            chosen cadence are mounted, so the form mirrors what the
            backend validator (``RoutineTaskSerializer.validate``)
            actually requires. */}
        {freq === 'weekly' && (
          <label className="ty-field">
            <span>On weekday</span>
            <select
              className="ty-select"
              required
              value={values.cron_weekly_day}
              onChange={set('cron_weekly_day')}
            >
              <option value="">Select a day…</option>
              {ROUTINE_WEEKDAYS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
        )}

        {freq === 'monthly' && (
          <label className="ty-field">
            <span>On day of month</span>
            <input
              className="ty-input"
              type="number"
              min={1}
              max={31}
              required
              value={values.cron_monthly_date}
              onChange={set('cron_monthly_date')}
              placeholder="1 – 31"
            />
          </label>
        )}

        {freq === 'annually' && (
          <>
            <label className="ty-field">
              <span>Date</span>
              <input
                className="ty-input"
                type="number"
                min={1}
                max={31}
                required
                value={values.cron_annually_date}
                onChange={set('cron_annually_date')}
                placeholder="1 – 31"
              />
            </label>
            <label className="ty-field">
              <span>Month</span>
              <select
                className="ty-select"
                required
                value={values.cron_annually_month}
                onChange={set('cron_annually_month')}
              >
                <option value="">Select a month…</option>
                {ROUTINE_MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>

      <div className="ty-form__row">
        <label className="ty-field">
          <span>Active from</span>
          <input
            className="ty-input"
            type="date"
            value={values.start_date}
            onChange={set('start_date')}
          />
        </label>
        <label className="ty-field">
          <span>Active until</span>
          <input
            className="ty-input"
            type="date"
            value={values.end_date_infinite ? '' : values.end_date}
            onChange={set('end_date')}
            disabled={values.end_date_infinite}
          />
          <label className="ty-field ty-field--check" style={{ marginTop: 6 }}>
            <input
              type="checkbox"
              checked={values.end_date_infinite}
              onChange={set('end_date_infinite')}
            />
            <span>Remain active always (no end date)</span>
          </label>
        </label>
        <label className="ty-field">
          <span>Estimated hours</span>
          <input
            className="ty-input"
            type="number"
            min={0}
            value={values.estimated_hours}
            onChange={set('estimated_hours')}
            placeholder="optional"
          />
        </label>
      </div>

      <div className="ty-form__row">
        {!solo ? (
        <label className="ty-field">
          <span>Team mode</span>
          <select
            className="ty-select"
            value={values.team}
            onChange={set('team')}
          >
            {TASK_TEAM.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        ) : null}

        <label className="ty-field">
          <span>Status</span>
          <select
            className="ty-select"
            value={values.status}
            onChange={set('status')}
          >
            {ROUTINE_STATUS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>

        {!solo ? (
        <label className="ty-field ty-field--check">
          <input
            type="checkbox"
            checked={values.approval_required}
            onChange={set('approval_required')}
          />
          <span>Approval required before tasks fire</span>
        </label>
        ) : null}
      </div>

      <div className="ty-form__row">
        <label className="ty-field" style={{ flex: 1 }}>
          <span>Assigned users *</span>
          {solo ? (
            <>
              <input className="ty-input" value="Assigned to you" disabled readOnly />
              <small className="ty-hint">
                Personal accounts keep every generated task assigned to you.
              </small>
            </>
          ) : usersLoading ? (
            <Loader label="Loading users…" />
          ) : (
            <select
              className="ty-select"
              multiple
              size={Math.min(6, Math.max(3, users.length))}
              value={values.users.map(String)}
              onChange={(e) => {
                const next = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                setValues((prev) => ({ ...prev, users: assigneesOrSelf(next, selfId) }));
              }}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {formatAssigneeLabel(u, selfId)}
                </option>
              ))}
            </select>
          )}
          {!solo ? (
          <small className="ty-hint">
            Defaults to Self. Hold ⌘/Ctrl to assign additional people.
            Each generated task will be assigned to everyone you pick here.
          </small>
          ) : null}
        </label>
      </div>

      <div className="ty-form__actions">
        <button
          type="submit"
          className="ty-btn"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : (initial ? 'Save changes' : 'Create routine')}
        </button>
      </div>
    </form>
  );
}
